import {
	Directive, ElementRef, HostListener, OnInit, OnDestroy
} from '@angular/core';

import { BsDropdownDirective } from 'ngx-bootstrap/dropdown';
import { Subscription } from 'rxjs';

/**
 * Override for ngx-bootstrap to add better keyboard controls.
 * Adds support for container=body.
 * Escape key closes menu if a menu item is focuses
 * Wrapping from bottom to top
 * handle focusin to keep focused item in sync with internal state
 */
@Directive({
	selector: '[vbDropdown]',
})
export class VbDropdownDirective implements OnInit, OnDestroy {

	private menuElement: HTMLElement;
	private subs: Subscription[] = [];
	private isOpen: boolean;
	private isEnterPressed = false;
	private isTabPressed = false;

	private get toggleBtn(): HTMLElement {
		return this.elementRef.nativeElement.querySelector('[dropdownToggle]');
	}

	constructor(
		private readonly dropdown: BsDropdownDirective,
		private readonly elementRef: ElementRef
	) {
		//Override arrowdown/arrowup hostbinding method.
		dropdown.navigationClick = (event: KeyboardEvent)=> { };
	}

	public ngOnInit(): void {
		this.subs.push(this.dropdown.onShown.subscribe(() => {
			setTimeout(() => {
				if(!this.menuElement && (this.dropdown as any)?._inlinedMenu?.rootNodes[0]) {
					this.menuElement = (this.dropdown as any)._inlinedMenu.rootNodes[0];
					this.menuElement?.addEventListener('focusout', e => this.onFocusOut(e));
				}
				const focusableElements = this.menuElement?.querySelectorAll('button, a, input, textarea, select, [tabindex="0"]');
				if (focusableElements?.length > 0 && this.isEnterPressed) {
					(focusableElements[0] as HTMLElement).focus();
				}
			}, 10);
		}));

		if(this.dropdown.container === 'body') {
			this.subs.push(this.dropdown.isOpenChange.subscribe(isOpen => {
				this.isOpen = isOpen;
				if(!this.isOpen) {
					return;
				}

				const menuEl = this.getMenuEl();
				menuEl?.addEventListener('keydown', e => {
					switch(e.code) {
						case 'Escape':
							this.dropdown.hide();
							break;
						case 'ArrowDown':
							this.moveFocus(false);
							break;
						case 'ArrowUp':
							this.moveFocus(true);
							break;
						default:
							return;
					}
					e.stopPropagation();
					e.preventDefault();
				});
			}));
		}
	}

	public ngOnDestroy(): void {
		this.subs.forEach(sub => sub.unsubscribe());
	}

	private moveFocus(backwards: boolean): void {
		const menu = this.getMenuEl();
		//Loop between menu items and toggle button
		const targets = [this.toggleBtn, ...Array.from(menu.querySelectorAll<HTMLElement>('.dropdown-item'))];
		const active = menu.ownerDocument.activeElement as HTMLElement;
		const next = findNextElement(active, targets, backwards);
		next?.focus();
	}

	@HostListener('keydown', ['$event'])
	public onKeydown(event) {
		if (event.code === 'Tab') {
			this.isTabPressed = true;
		}
		if (event.code === 'Enter') {
			this.isEnterPressed = true;
		}
	}

	@HostListener('keyup', ['$event'])
	public onKeyup(event) {
		if (event.code === 'Tab') {
			this.isTabPressed = false;
		}
		if (event.code === 'Enter') {
			this.isEnterPressed = false;
		}
	}

	private onFocusOut($event) {
		if ($event.relatedTarget !== null && !this.menuElement.contains($event.relatedTarget) && this.isTabPressed) {
			this.dropdown.hide();
		}
	};

	private getMenuEl(): HTMLElement {
		return (this.dropdown as any)._dropdown?.instance?._element?.nativeElement;
	}
}


// focus on the next or previous element in the menu. Loops around to beginning when the end is reached.
function findNextElement(active: HTMLElement, targets: HTMLElement[], isReverse: boolean): HTMLElement {
	let allRef = targets;
	let nodePosition: any = Node.DOCUMENT_POSITION_FOLLOWING;
	if(!allRef.length) {
		return;
	}

	if(isReverse) {
		allRef = allRef.reverse();
		nodePosition = Node.DOCUMENT_POSITION_PRECEDING;
	}

	return allRef.find(el => active.compareDocumentPosition(el) & nodePosition)
		|| allRef[0];
}
